import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { StoreService } from '@core/services/store/store.service';
import { of, mergeMap, withLatestFrom, filter, map, forkJoin } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
	LOCAL_CHANNEL_FIND,
	STORE_FIND,
	STORE_FIND_SUCCESS,
	STORE_FAILED,
	CHECK_BUSINESS_DAY,
	CHECK_BUSINESS_DAY_SUCCESS,
	CHECK_BUSINESS_DAY_FAILURE,
	STORE_CLOSED,
	STORE_FIND_BY_ID,
	STORE_UPDATE_LOADING,
	LOCAL_CHANNEL_FIND_SESION,
	STORE_FIND_LOCATION,
	STORE_FIND_LOCATION_SUCCESS,
	STORE_FIND_LOCATION_FAILURE,
	STORE_FIND_ALL_SUCCESS,
	STORE_FIND_ALL,
	STORE_IS_MULTI_UPDATE,
	WORK_DAY_FIND_SUCCESS,
	MENU_FIND_ALL,
} from '@store/actions';
import { WorkDayService } from '@core/services';
import { Store } from '@ngrx/store';
import { selectSlug, selectStore } from '../selectors';
import { Store as StoreModel } from '@core/models';
import { IResponseBadRequest, IResponseSuccess } from '@core/interfaces';
import { GeolocationService } from '@core/services';
import { Router } from '@angular/router';
import { transformStoreData } from '@core/helpers';
import { getStoreList, hasStoreAddress, isBusinessDayClosed, isMultiStore } from '@core/domain/store';

@Injectable()
export class StoreEffects {
  
	constructor(
		private store: Store,
		private actions$: Actions,
		private storeService: StoreService,
		private workDayService: WorkDayService,
		private geolocationService: GeolocationService,
		private route: Router
	) {}

	find$ = createEffect(() =>
		this.actions$.pipe(
			ofType(STORE_FIND),
			withLatestFrom(this.store.select(selectSlug)),
			filter(([_, slug]) => !!slug),
			map(([, slug]) => slug as string),
			switchMap((slug) => {
				this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));

				return this.storeService.find(slug).pipe(
					mergeMap(({ data }) => {
						const [store] = data as unknown as StoreModel[];

						if (hasStoreAddress(store)) {
							this.geolocationService.getCoordinatesFromAddress(store.ubicacion.direccion);
						}

						return [
							STORE_FIND_SUCCESS({ store: transformStoreData(store) }),
							STORE_UPDATE_LOADING({ loading: false }),
							STORE_FIND_LOCATION(),
							LOCAL_CHANNEL_FIND(),
							MENU_FIND_ALL(),
							LOCAL_CHANNEL_FIND_SESION({ sesion: 'STOCK' }),
						];
					}),
					catchError(() => {
						this.route.navigate(['/404']);

						return of();
					})
				);
			})
		)
	);

	findLocation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(STORE_FIND_LOCATION),
			withLatestFrom(this.store.select(selectStore)),
			filter(([_, store]) => !!store),
			map(([_, store]) => {
				return { store: store as unknown as StoreModel };
			}),
			mergeMap(({ store }) => {
				if (!hasStoreAddress(store)) {
					return of(
						STORE_FIND_LOCATION_FAILURE({
							error: { message: 'No hay dirección disponible' } as IResponseBadRequest,
						})
					);
				}

        return this.geolocationService
          .getCoordinatesFromAddress(store.ubicacion.direccion)
          .pipe(
            map((location) => STORE_FIND_LOCATION_SUCCESS({ location })),
            catchError((error) =>
              of(
                STORE_FIND_LOCATION_FAILURE({
                  error: { message: error?.message || 'Error al obtener la ubicación' } as IResponseBadRequest,
                })
              )
            )
          );
      })
    )
  );

	findAll$ = createEffect(() =>
		this.actions$.pipe(
			ofType(STORE_FIND_ALL),
			switchMap((action) => {
				this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));
				return this.storeService
				.findAllStores({ slug: action.accountSlug, limit: 100 })
				.pipe(
					switchMap(({ data }: IResponseSuccess<StoreModel>) => {
						const storeList = getStoreList(data);

						const storesWithCoords$ = storeList.map(store => {
							if (hasStoreAddress(store)) {
								return this.geolocationService.getCoordinatesFromAddress(store.ubicacion.direccion, store.ubicacion?.pais).pipe(
									map(location => ({
										...store,
										location,
									})),
									catchError(() => of({ ...store, location: null }))
								);
							} else {
								return of({ ...store, location: null });
							}
						});

						return forkJoin(storesWithCoords$).pipe(
							mergeMap(storesWithCoords => [
								STORE_FIND_ALL_SUCCESS({ storeList: storesWithCoords }),
								STORE_UPDATE_LOADING({ loading: false }),
								STORE_IS_MULTI_UPDATE({
									isMultiStore: isMultiStore(storesWithCoords),
								}),
							])
						);
					}),
					catchError(error => {
						this.route.navigate(['/404']);
						return of(
							STORE_UPDATE_LOADING({ loading: false }),
							STORE_FAILED({
								error: error.message || 'Error al cargar tiendas',
							})
						);
					})
				);
			})
		)
	);

	findById$ = createEffect(() =>
		this.actions$.pipe(
			ofType(STORE_FIND_BY_ID),
			withLatestFrom(this.store.select(selectStore)),
			filter(([_, store]) => !!store),
			map(([_, store]) => {
				return { storeId: store?.idLocal as string };
			}),
			switchMap(({ storeId }) => {
				this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));
				return this.storeService.findById(storeId).pipe(
					mergeMap(({ data }) => {
						const [store] = data;
						return [STORE_FIND_SUCCESS({ store }), STORE_UPDATE_LOADING({ loading: false })];
					}),
					catchError(error => of(STORE_FAILED({ error })))
				);
			})
		)
	);

	checkBusinessDay$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CHECK_BUSINESS_DAY),
			withLatestFrom(this.store.select(selectStore)),
      filter(([_, store]) => !!store && !!store?.idLocal),
      map(([_, store]) => ({ ...store, idLocal: store!.idLocal as string } as StoreModel)),
			switchMap(({ idLocal }) => {
				return this.workDayService.obtenerWorkDayActual(idLocal).pipe(
					switchMap((workDay) => {
						if (isBusinessDayClosed(workDay)) {
							return [
								CHECK_BUSINESS_DAY_SUCCESS({ workDay }),
								WORK_DAY_FIND_SUCCESS({ workDay: workDay }),
								STORE_CLOSED({ message: 'La tienda está cerrada' })
							];
						}
						return [
							CHECK_BUSINESS_DAY_SUCCESS({ workDay }),
							WORK_DAY_FIND_SUCCESS({ workDay: workDay })
						];
					}),
					catchError((error) => of(CHECK_BUSINESS_DAY_FAILURE({ error })))
				);
			})
		)
	);

	workDayClosed$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(WORK_DAY_FIND_SUCCESS),
				filter(({ workDay }) => {
					return isBusinessDayClosed(workDay);
				}),
				tap(() => {
					this.workDayService.handleStoreClosed();
				})
			),
		{ dispatch: false }
	);
}
